import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ConsolidationDismantlePreview, ConsolidationDismantleService } from './consolidation-dismantle.service';

@Component({
  selector: 'app-dismantle-consolidation-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule],
  template: `
    <h2 mat-dialog-title>Dismantle Consolidation {{ data.consolidationId }}?</h2>
    <mat-dialog-content>
      <p *ngIf="loading" role="status">Checking the consolidation…</p>
      <p *ngIf="error" class="error" role="alert">{{ error }}</p>
      <ng-container *ngIf="preview">
        <p>Remove this consolidation and its generated parent ladings. The original ladings stay in place with their shipment details, carrier, status and history.</p>
        <div class="record-lists">
          <section>
            <h3>Remove · {{ preview.remove.length }} parent ladings</h3>
            <ul><li *ngFor="let row of preview.remove"><strong>{{ row.reference }}</strong> <span>{{ row.kind }}</span></li></ul>
          </section>
          <section class="keep">
            <h3>Keep · {{ preview.keep.length }} original ladings</h3>
            <ul><li *ngFor="let row of preview.keep"><strong>{{ row.reference }}</strong></li></ul>
            <p *ngIf="!preview.keep.length">No original ladings are currently attached.</p>
          </section>
        </div>
        <p class="scope">This removes the consolidation from ULAAP. UFOS orders are unchanged.</p>
        <div *ngIf="preview.blocking_reasons.length" class="error" role="alert">
          <strong>This consolidation needs review before it can be dismantled.</strong>
          <ul><li *ngFor="let reason of preview.blocking_reasons">{{ reason }}</li></ul>
        </div>
      </ng-container>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button (click)="dialogRef.close()" [disabled]="saving" cdkFocusInitial>Cancel</button>
      <button mat-raised-button color="warn" (click)="confirm()"
        [disabled]="loading || saving || !preview?.can_dismantle || !!error">
        {{ saving ? 'Dismantling…' : 'Dismantle Consolidation' }}
      </button>
    </mat-dialog-actions>
  `,
  styles: [`
    :host { display: block; }
    h2 { margin: 0; }
    p { line-height: 1.5; }
    .record-lists { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
    section { padding: 12px 16px; background: #fff1f2; border: 1px solid #fecdd3; border-radius: 8px; }
    section.keep { background: #f0fdf4; border-color: #bbf7d0; }
    h3 { margin: 0 0 8px; font-size: 15px; font-weight: 600; }
    ul { margin: 0; padding-left: 20px; }
    li { margin: 5px 0; overflow-wrap: anywhere; }
    li span { margin-left: 6px; color: #64748b; font-size: 12px; }
    .scope { color: #64748b; font-size: 13px; }
    .error { color: #991b1b; background: #fef2f2; padding: 12px; border-radius: 6px; }
    mat-dialog-actions { padding: 12px 24px 20px; gap: 8px; }
    @media (max-width: 560px) { .record-lists { grid-template-columns: 1fr; } }
  `]
})
export class DismantleConsolidationDialogComponent implements OnInit, OnDestroy {
  preview?: ConsolidationDismantlePreview;
  loading = true;
  saving = false;
  error = '';
  private readonly destroyed$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { consolidationId: number },
    public dialogRef: MatDialogRef<DismantleConsolidationDialogComponent>,
    private service: ConsolidationDismantleService
  ) {}

  ngOnInit(): void {
    this.service.preview(this.data.consolidationId).pipe(takeUntil(this.destroyed$)).subscribe({
      next: preview => { this.preview = preview; this.loading = false; },
      error: error => {
        this.error = error.error?.message || 'The consolidation could not be checked. Close this dialog and try again.';
        this.loading = false;
      }
    });
  }

  confirm(): void {
    if (!this.preview?.can_dismantle || this.saving || this.error) return;
    this.saving = true;
    this.dialogRef.disableClose = true;
    this.service.dismantle(this.preview).pipe(takeUntil(this.destroyed$)).subscribe({
      next: response => {
        if (response.ok) this.dialogRef.close(response.data);
        else this.failed('The response could not be confirmed. Refresh the list before retrying.');
      },
      error: error => this.failed(error.error?.message || 'The result could not be confirmed. Refresh the list before retrying.')
    });
  }

  private failed(message: string): void {
    this.error = message;
    this.saving = false;
    this.dialogRef.disableClose = false;
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
